import './professional-caregiver-invite-form.scss';
import {useContext, useEffect, useState} from 'react';
import {AffiliationType} from '../../backend/enums/affiliation-type';
import {InviteContactMethod} from '../../enums/invite-contact-method';
import {ContentPage} from '../content-page/content-page';
import {PageHeader} from '../page-header/page-header';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {useHistory} from 'react-router-dom';
import {TextInput} from '../text-input/text-input';
import {SelectInput} from '../select-input/select-input';
import {PhoneNumberInput} from '../phone-number-input/phone-number-input';
import {Button} from '@mui/material';
import {AlertModal} from '../alert-modal/alert-modal';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {EMAIL_REGEX} from '../../utils/constants/regex';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {OfficeContext} from '../../contexts/office/context';
import {OfficeContextType} from '../../contexts/office/types';
import {AffiliationInviteDataPostInvite, PostInvite} from '../../backend/requests/post-invite';
import {AffiliationInviteData} from '../../backend/interfaces/affiliation-invite-data';
import {InvitesRepository} from '../../repositories/invites';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {InviteType} from '../../backend/enums/invite-type';
import {InviteSenderType} from '../../backend/enums/invite-sender-type';
import {inviteContactMethods} from '../../utils/data/invite-contact-methods';
import {ProfessionalCaregiverInviteFormType} from '../../enums/professional-caregiver-invite-form-type';
import {
  FlatSearchSelectInput,
  SearchSelectOption,
} from '../flat-search-select-input/flat-search-select-input';
import {EntitiesRepository} from '../../repositories/entities';
import {validateMobilePhoneNumber} from '../../utils/functions/validate-mobile-phone-number';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

interface SearchSelectInputItem {
  id: string;
  name: string;
  picture?: string;
}

/**
 * Form to invite a professional caregiver.
 *
 * It uses the office context to handle the form state.
 *
 * Permissions are given by props.
 */
export const ProfessionalCaregiverInviteForm = (props: {
  formType: ProfessionalCaregiverInviteFormType;
  senderId: string;
  senderType: InviteSenderType;
  redirectUrl: string;
  useSmallerTitle: boolean;
  showExitButton: boolean;
  entity?: {
    entityId: string;
    entityName: string;
    entityPicture?: string;
  };
}) => {
  // Hold state
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [submitDialogOpened, setSubmitDialogOpened] = useState<boolean>(false);
  const [dataEntered, setDataEntered] = useState<boolean>(false);
  const [name, setName] = useState<string | undefined>();
  const [nameError, setNameError] = useState<string | undefined>();
  const [showNameError, setShowNameError] = useState<boolean>(false);
  const [email, setEmail] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [countryCode, setCountryCode] = useState<{
    phone: string;
    code: string;
  }>({
    phone: '+351',
    code: 'PT',
  });
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>();
  const [showPhoneNumberError, setShowPhoneNumberError] = useState<boolean>(false);
  const [contactMethod, setContactMethod] = useState<InviteContactMethod>(
    InviteContactMethod.Email
  );
  const [affiliationType, setAffiliationType] = useState<AffiliationType>(AffiliationType.Operator);
  const [selectedEntity, setSelectedEntity] = useState<SearchSelectInputItem | undefined>(
    undefined
  );
  const entity: SearchSelectInputItem | undefined = props.entity
    ? {
        id: props.entity.entityId,
        name: props.entity.entityName,
        picture: props.entity.entityPicture,
      }
    : selectedEntity;
  const [entityError, setEntityError] = useState<string | undefined>();
  const [showEntityError, setShowEntityError] = useState<boolean>(false);

  // Get context
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Translations
  const {t} = useTranslation();

  // Get history
  let history = useHistory();

  // On mount
  useEffect(() => {
    // Set the submit button state
    officeContext.setProfessionalCaregiverInviteFormSubmitButtonState(SubmitButtonState.Normal);

    // Clean up
    return () => {
      // Log
      console.debug('🧹 Professional caregiver form button state cleaned in office context.');

      // Clear button state
      officeContext.clearProfessionalCaregiverInviteFormSubmitButtonState();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation
  useEffect(() => {
    // ----- Fields validation -----
    // Holds validation
    let nameValid: boolean = false;
    let phoneNumberValid: boolean = false;
    let emailValid: boolean = false;
    let entityValid: boolean = false;

    // Name validation
    if (name && name.trim().length > 0) {
      // No errors
      if (nameError !== undefined) {
        setNameError(undefined);
      }

      // Valid
      nameValid = true;
    } else {
      // Errors
      if (showNameError) {
        setNameError(
          t(translations.professional_caregiver_invite_form.errors.form_validation_invalid_field)
        );
      }

      // Invalid
      nameValid = false;
    }

    // Entity validation
    if (!entity?.id) {
      // Errors
      if (showEntityError) {
        setEntityError(
          t(translations.professional_caregiver_invite_form.errors.form_validation_invalid_field)
        );
      }

      // Invalid
      entityValid = false;
    } else {
      // No errors
      if (entityError !== undefined) {
        setEntityError(undefined);
      }

      // Valid
      entityValid = true;
    }

    if (contactMethod === InviteContactMethod.Email) {
      // Email validation
      if (email && email.trim().length > 0) {
        if (EMAIL_REGEX.test(email)) {
          // No errors
          if (emailError !== undefined) {
            setEmailError(undefined);
          }

          // Valid
          emailValid = true;
        } else {
          // Errors
          if (showEmailError) {
            setEmailError(
              t(
                translations.professional_caregiver_invite_form.errors.form_validation_invalid_email
              )
            );
          }

          // Invalid
          emailValid = false;
        }
      } else {
        // Errors
        if (showEmailError) {
          setEmailError(
            t(translations.professional_caregiver_invite_form.errors.form_validation_invalid_field)
          );
        }

        // Invalid
        emailValid = false;
      }
    } else if (contactMethod === InviteContactMethod.MobilePhoneNumber) {
      // Phone number validation
      if (phoneNumber && phoneNumber.trim().length > 0) {
        // Validate the phone number
        const phoneNumberValidation = validateMobilePhoneNumber(
          `${countryCode.phone}${phoneNumber.trim()}`
        );

        if (
          phoneNumberValidation.isMobilePhoneNumber === true &&
          phoneNumberValidation.isValidPhoneNumber === true
        ) {
          // No errors
          if (phoneNumberError !== undefined) {
            setPhoneNumberError(undefined);
          }

          // Valid
          phoneNumberValid = true;
        } else if (
          phoneNumberValidation.isMobilePhoneNumber === false &&
          phoneNumberValidation.isValidPhoneNumber === true
        ) {
          // Errors
          if (showPhoneNumberError) {
            setPhoneNumberError(
              t(
                translations.professional_caregiver_invite_form.errors
                  .form_validation_not_mobile_phone_number
              )
            );
          }

          // Invalid
          phoneNumberValid = false;
        } else {
          // Errors
          if (showPhoneNumberError) {
            setPhoneNumberError(
              t(
                translations.professional_caregiver_invite_form.errors
                  .form_validation_invalid_mobile_phone_number
              )
            );
          }

          // Invalid
          phoneNumberValid = false;
        }
      } else {
        // Errors
        if (showPhoneNumberError) {
          setPhoneNumberError(
            t(translations.professional_caregiver_invite_form.errors.form_validation_invalid_field)
          );
        }

        // Invalid
        phoneNumberValid = false;
      }
    } else {
      // Invalid
      phoneNumberValid = false;
      emailValid = false;
    }
    // ----- Fields validation -----

    // ----- Data entered -----
    if (
      (name && name.trim().length > 0) ||
      (props.formType === ProfessionalCaregiverInviteFormType.SystemAdministrator &&
        !props.entity &&
        entity) ||
      (contactMethod === InviteContactMethod.Email && email && email.trim().length > 0) ||
      (contactMethod === InviteContactMethod.MobilePhoneNumber &&
        phoneNumber &&
        phoneNumber.trim().length > 0) ||
      contactMethod !== InviteContactMethod.Email ||
      affiliationType !== AffiliationType.Operator
    ) {
      // Data was entered
      setDataEntered(true);
    } else {
      // No data was entered
      setDataEntered(false);
    }
    // ----- Data entered -----

    // ----- Submit button validation -----
    if (contactMethod === InviteContactMethod.Email) {
      if (nameValid && emailValid && entityValid) {
        // Enable submit button
        setSubmitButtonDisabled(false);
      } else {
        // Disable submit button
        setSubmitButtonDisabled(true);
      }
    } else if (contactMethod === InviteContactMethod.MobilePhoneNumber) {
      if (nameValid && phoneNumberValid && entityValid) {
        // Enable submit button
        setSubmitButtonDisabled(false);
      } else {
        // Disable submit button
        setSubmitButtonDisabled(true);
      }
    } else {
      // Disable submit button
      setSubmitButtonDisabled(true);
    }
    // ----- Submit button validation -----

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    name,
    affiliationType,
    contactMethod,
    email,
    phoneNumber,
    countryCode,
    entity,
    nameError,
    emailError,
    phoneNumberError,
    entityError,
    showNameError,
    showEmailError,
    showPhoneNumberError,
    showEntityError,
    props.formType,
    props.entity,
  ]);

  // Handle submit dialog opened state
  const handleSubmitDialogOpened = (opened: boolean) => {
    // Change dialog state
    setSubmitDialogOpened(opened);
  };

  // Save changes
  const handleSaveChanges = (event: any) => {
    event.preventDefault();

    // Open confirm dialog
    setSubmitDialogOpened(true);
  };

  // Submit
  const handleSubmit = async () => {
    try {
      // Close confirm dialog
      setSubmitDialogOpened(false);

      // Update submit button state
      officeContext.setProfessionalCaregiverInviteFormSubmitButtonState(SubmitButtonState.Loading);

      // Check if the entity is set
      if (!entity?.id) {
        throw new Error('No entity set.');
      }

      // Request body
      const requestBody: PostInvite<AffiliationInviteDataPostInvite> = {
        type: InviteType.Affiliation,
        senderType: props.senderType,
        senderId: props.senderId,
        data: {
          targetEntityId: entity.id,
          type: affiliationType,
          destinationName: name?.trim(),
          destinationPhoneNumber:
            contactMethod === InviteContactMethod.MobilePhoneNumber
              ? `${countryCode.phone}${phoneNumber?.trim()}`
              : undefined,
          destinationEmail: contactMethod === InviteContactMethod.Email ? email?.trim() : undefined,
        },
      };

      // Send invite
      await InvitesRepository.instance.createInvite<AffiliationInviteData>(requestBody);

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.professional_caregiver_invite_form.success.success_message, {
          name: name,
        }),
      });

      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated.
      setTimeout(() => {
        // Redirect
        history.push(props.redirectUrl);
      }, 500);
    } catch (error) {
      // Log
      console.debug('🔴 Could not send invite.', error);

      // Update submit button state
      officeContext.setProfessionalCaregiverInviteFormSubmitButtonState(SubmitButtonState.Error);
    }
  };

  // Exit the screen
  const handleExit = () => {
    // Redirect
    history.push(props.redirectUrl);
  };

  // Handle change name input
  const handleChangeName = (value: string) => {
    setName(value);
  };

  // Handle change affiliation type input
  const handleChangeAffiliationType = (affiliation?: string) => {
    if (affiliation) {
      setAffiliationType(affiliation as AffiliationType);
    }
  };

  // Handle change contact method input
  const handleChangeContactMethod = (contact?: string) => {
    if (contact) {
      // Change the contact method
      setContactMethod(contact as InviteContactMethod);

      // Reset email, phone number, and country calling code
      setEmail(undefined);
      setPhoneNumber(undefined);
      setCountryCode({
        phone: '+351',
        code: 'PT',
      });
    }
  };

  // Handle change email input
  const handleChangeEmail = (value: string) => {
    setEmail(value);
  };

  // Handle change country code input
  const handleChangeCountryCode = (value: {phone: string; code: string}) => {
    setCountryCode(value);
  };

  // Handle change phone number input
  const handleChangePhoneNumber = (value: string) => {
    setPhoneNumber(value);
  };

  // Handle change the entity
  const handleChangeEntity = (value?: SearchSelectOption<SearchSelectInputItem>) => {
    setSelectedEntity(value ? value.item : undefined);
  };

  // Handle blur name input
  const handleBlurName = () => {
    if (!showNameError) {
      setShowNameError(true);
    }
  };

  // Handle blur email input
  const handleBlurEmail = () => {
    if (!showEmailError) {
      setShowEmailError(true);
    }
  };

  // Handle blur phone number input
  const handleBlurPhoneNumber = () => {
    if (!showPhoneNumberError) {
      setShowPhoneNumberError(true);
    }
  };

  // Handle blur entity
  const handleBlurEntity = () => {
    if (!showEntityError) {
      setShowEntityError(true);
    }
  };

  // Fetch entities
  const handleFetchEntities = async (
    limit: number,
    skip: number,
    search?: string
  ): Promise<{data: Array<SearchSelectOption<SearchSelectInputItem>>; total: number}> => {
    // Get entities
    const response = await EntitiesRepository.instance.get(skip, limit, search);

    // Return
    return {
      data: response.data.map((obj) => {
        return {
          id: obj.id || '',
          name: obj.name || '',
          item: {id: obj.id || '', name: obj.name || '', picture: obj.picture},
          avatar: obj.picture,
        };
      }),
      total: response.totalCount,
    };
  };

  // Affiliation type options
  let affiliationOptions: Array<{value: AffiliationType; label: string}> = [
    {value: AffiliationType.Operator, label: t(translations.affiliation_types.operator)},
    {
      value: AffiliationType.Manager,
      label: t(translations.affiliation_types.manager),
    },
  ];
  if (
    props.formType === ProfessionalCaregiverInviteFormType.ProfessionalCaregiver_Administrator ||
    props.formType === ProfessionalCaregiverInviteFormType.SystemAdministrator
  ) {
    affiliationOptions.push({
      value: AffiliationType.Administrator,
      label: t(translations.affiliation_types.administrator),
    });
  }

  return (
    <ContentPage>
      <form onSubmit={handleSaveChanges}>
        <PageHeader
          title={t(translations.professional_caregiver_invite_form.title)}
          isSmallTitle={props.useSmallerTitle}
          leftButtons={
            props.showExitButton ? (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloseIcon />}
                onClick={handleExit}
                disabled={
                  officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
                  SubmitButtonState.Loading
                }
              >
                {t(translations.professional_caregiver_invite_form.exit_button)}
              </Button>
            ) : undefined
          }
          rightButtons={
            <SubmitButton
              disabled={submitButtonDisabled}
              state={
                officeContext.state.professionalCaregiverInviteFormSubmitButtonState ||
                SubmitButtonState.Normal
              }
              onStateChange={(newState: SubmitButtonState) => {
                officeContext.setProfessionalCaregiverInviteFormSubmitButtonState(newState);
              }}
              normalStateLabel={t(translations.professional_caregiver_invite_form.save_button)}
              loadingStateLabel={t(translations.professional_caregiver_invite_form.saving_button)}
              successStateLabel={t(
                translations.professional_caregiver_invite_form.success.save_success_button
              )}
              errorStateLabel={t(
                translations.professional_caregiver_invite_form.errors.error_on_save_button
              )}
              endIcon={<SendIcon />}
              type="submit"
            />
          }
        />
        {props.formType === ProfessionalCaregiverInviteFormType.SystemAdministrator &&
          !props.entity && (
            <FlatSearchSelectInput<SearchSelectInputItem>
              label={t(translations.professional_caregiver_invite_form.entity_input)}
              value={
                entity
                  ? {
                      id: entity.id,
                      name: entity.name,
                      item: entity,
                      avatar: entity.picture,
                    }
                  : undefined
              }
              error={entityError}
              required={true}
              handleChange={handleChangeEntity}
              handleFetchData={handleFetchEntities}
              handleBlur={handleBlurEntity}
              fetchWhenEmpty={true}
              disableClear={true}
              disabled={
                officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
                SubmitButtonState.Loading
              }
              avatar={{
                isIcon: false,
                type: 'entity',
              }}
            />
          )}
        <TextInput
          className={
            props.formType === ProfessionalCaregiverInviteFormType.SystemAdministrator &&
            !props.entity
              ? 'professional-caregiver-invite-form-field'
              : ''
          }
          label={t(translations.professional_caregiver_invite_form.name_input)}
          value={name || ''}
          handleChange={handleChangeName}
          handleBlur={handleBlurName}
          error={nameError}
          disabled={
            officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
            SubmitButtonState.Loading
          }
          required={true}
          type="text"
        />
        <SelectInput
          id="professional-caregiver-invite-form-affiliation-type-select"
          className="professional-caregiver-invite-form-field"
          label={t(translations.professional_caregiver_invite_form.affiliation_type_input)}
          value={affiliationType}
          handleChange={handleChangeAffiliationType}
          items={affiliationOptions}
          disabled={
            officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
            SubmitButtonState.Loading
          }
          required={true}
        />
        <SelectInput
          id="professional-caregiver-invite-form-contact-method-select"
          className="professional-caregiver-invite-form-field"
          label={t(translations.professional_caregiver_invite_form.contact_method_input)}
          value={contactMethod}
          handleChange={handleChangeContactMethod}
          items={inviteContactMethods.map((obj) => {
            return {label: t(obj.translation), value: obj.type};
          })}
          disabled={
            officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
            SubmitButtonState.Loading
          }
          required={true}
        />
        {contactMethod === InviteContactMethod.Email && (
          <TextInput
            className="professional-caregiver-invite-form-field"
            label={t(translations.professional_caregiver_invite_form.email_input)}
            value={email || ''}
            handleChange={handleChangeEmail}
            handleBlur={handleBlurEmail}
            error={emailError}
            disabled={
              officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
              SubmitButtonState.Loading
            }
            required={true}
            type="email"
          />
        )}
        {contactMethod === InviteContactMethod.MobilePhoneNumber && (
          <PhoneNumberInput
            className="professional-caregiver-invite-form-field"
            label={t(translations.professional_caregiver_invite_form.mobile_phone_number_input)}
            countryCode={countryCode}
            phoneNumber={phoneNumber || ''}
            handleChangeCountryCode={handleChangeCountryCode}
            handleChangePhoneNumber={handleChangePhoneNumber}
            handleBlur={handleBlurPhoneNumber}
            error={phoneNumberError}
            disabled={
              officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
              SubmitButtonState.Loading
            }
            required={true}
          />
        )}
      </form>
      <AlertModal
        id="confirm-professional-caregiver-invitation"
        opened={submitDialogOpened}
        title={t(translations.professional_caregiver_invite_form.save_dialog_title, {
          name: name,
        })}
        content={t(translations.professional_caregiver_invite_form.save_dialog_message, {
          name: name,
          entity: entity?.name || '',
        })}
        handleClose={() => handleSubmitDialogOpened(false)}
        actions={[
          {
            label: t(
              translations.professional_caregiver_invite_form.save_dialog_action_cancel_button
            ),
            type: 'neutral',
            handleClick: () => handleSubmitDialogOpened(false),
          },
          {
            label: t(
              translations.professional_caregiver_invite_form.save_dialog_action_save_button
            ),
            type: 'nondestructive',
            handleClick: handleSubmit,
          },
        ]}
      />
      <RouterPrompt
        when={
          dataEntered &&
          officeContext.state.professionalCaregiverInviteFormSubmitButtonState !==
            SubmitButtonState.Loading
        }
        subtitle={t(translations.professional_caregiver_invite_form.router_prompt_title)}
        message={t(translations.professional_caregiver_invite_form.router_prompt_message)}
        onOK={() => true}
        onCancel={() => false}
        toastNotification={{
          severity: 'info',
          message: t(translations.professional_caregiver_invite_form.cancel_toast),
        }}
      />
    </ContentPage>
  );
};
